import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import {
  ComputerDesktopIcon,
  AdjustmentsHorizontalIcon,
  ChartBarIcon,
  InformationCircleIcon,
  ArrowRightEndOnRectangleIcon,
  UserCircleIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import packageJson from '../../package.json';
import { useAuth } from 'auth/AuthContext';
import { MANAGEMENT_TABS, DASHBOARD_TABS } from 'constants/navs';
import logo from 'assets/ghapp_logo.png';
import { getDefaultPathByPermissions } from 'utils/nav.utils';

const useActiveNav = () => {
  const location = useLocation();

  return ({ _isActive }, pathPrefix = '') => {
    const isSectionActive = pathPrefix && location.pathname.startsWith(pathPrefix);
    return `group flex items-center gap-2 px-4 py-2 transition-all rounded-lg ${_isActive || isSectionActive ? 'bg-white text-greenPrimary font-semibold' : 'text-white hover:bg-white/10'}`;
  };
};

const NavTopBar = () => {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const isActiveNav = useActiveNav();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isLangMenuOpen, setIsLangMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const langMenuRef = useRef(null);
  const { t, i18n } = useTranslation();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
      if (langMenuRef.current && !langMenuRef.current.contains(event.target)) {
        setIsLangMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const toggleLangMenu = () => {
    setIsLangMenuOpen(!isLangMenuOpen);
  };

  const changeLanguage = lng => {
    i18n.changeLanguage(lng);
    localStorage.setItem('language', lng);
    setIsLangMenuOpen(false);
  };

  const languages = {
    'en-US': { name: 'English', flag: '🇺🇸' },
    'es-MX': { name: 'Español', flag: '🇲🇽' }
  };

  const getNavLinkClass = pathPrefix => props => `${isActiveNav(props, pathPrefix)} group`;

  return (
    <header className="fixed top-0 left-0 right-0 h-16 z-30 bg-greenPrimary shadow-md">
      <div className="flex items-center justify-between h-full px-6">
        {/* Logo */}
        <div className="flex items-center gap-3">
          <img
            src={logo}
            alt="App Logo"
            className="h-10 w-auto hover:scale-105 transition-transform duration-200"
          />
          <span className="text-white font-bold text-lg hidden md:block">Greenhouses.App</span>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-grow flex justify-center">
          <ul className="flex items-center gap-2">
            <li>
              <NavLink
                to={getDefaultPathByPermissions(DASHBOARD_TABS, '/dashboard', user.permissions)}
                className={getNavLinkClass('/dashboard')}
              >
                <ComputerDesktopIcon className="w-5 h-5" />
                <span className="hidden sm:inline">{t('nav.dashboard')}</span>
              </NavLink>
            </li>
            <li>
              <NavLink
                to={getDefaultPathByPermissions(MANAGEMENT_TABS, '/management', user.permissions)}
                className={getNavLinkClass('/management')}
              >
                <AdjustmentsHorizontalIcon className="w-5 h-5" />
                <span className="hidden sm:inline">{t('nav.management')}</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/reports" className={getNavLinkClass('/reports')}>
                <ChartBarIcon className="w-5 h-5" />
                <span className="hidden sm:inline">{t('nav.reports')}</span>
              </NavLink>
            </li>
            <li>
              <NavLink to="/help" className={getNavLinkClass('/help')}>
                <InformationCircleIcon className="w-5 h-5" />
                <span className="hidden sm:inline">{t('nav.help')}</span>
              </NavLink>
            </li>
          </ul>
        </nav>

        {/* User Info & Logout */}
        <div className="flex items-center gap-4">
          {/* Language Selector */}
          <div className="relative" ref={langMenuRef}>
            <button
              onClick={toggleLangMenu}
              className="flex items-center gap-2 px-3 py-2 text-white hover:bg-white/10 rounded-lg transition-all"
            >
              <span className="hidden lg:inline text-sm">{languages[i18n.language]?.flag}</span>
              <ChevronDownIcon
                className={`w-4 h-4 transition-transform ${isLangMenuOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Language Dropdown */}
            {isLangMenuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50">
                {Object.entries(languages).map(([code, { name, flag }]) => (
                  <button
                    key={code}
                    onClick={() => changeLanguage(code)}
                    className={`w-full flex items-center gap-3 px-4 py-3 text-left text-sm hover:bg-gray-100 transition-colors ${
                      i18n.language === code
                        ? 'bg-gray-50 text-greenPrimary font-semibold'
                        : 'text-gray-700'
                    }`}
                  >
                    <span className="text-xl">{flag}</span>
                    <span>{name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative" ref={userMenuRef}>
            <button
              onClick={toggleUserMenu}
              className="flex items-center gap-2 px-3 py-2 text-white hover:bg-white/10 rounded-lg transition-all"
            >
              <UserCircleIcon className="w-5 h-5" />
              <span className="hidden lg:inline text-sm">
                {user.firstName} {user.lastName}
              </span>
              <ChevronDownIcon
                className={`w-4 h-4 transition-transform ${isUserMenuOpen ? 'rotate-180' : ''}`}
              />
            </button>

            {/* Dropdown Menu */}
            {isUserMenuOpen && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-2 z-50">
                <div className="px-4 py-3 border-b border-gray-200">
                  <p className="text-sm font-semibold text-gray-900">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">{user.email || 'User'}</p>
                </div>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors"
                >
                  <ArrowRightEndOnRectangleIcon className="w-5 h-5" />
                  <span>{t('nav.logout')}</span>
                </button>
              </div>
            )}
          </div>
          <div className="px-4 py-2">
            <p className="text-xs text-gray-400">
              {t('nav.version')} {packageJson.version}
            </p>
          </div>
        </div>
      </div>
    </header>
  );
};

export default NavTopBar;
